import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Download, CheckCircle, Link as LinkIcon } from 'lucide-react'
import { connectionTokenApi, BACKEND_URL } from '../lib/api'
import type { ConnectionTokenResponse } from '../lib/api'

export const Route = createFileRoute('/school/$schoolId/setup')({
  head: () => ({
    meta: [{ title: 'App Setup | Unbreakable' }],
  }),
  component: SchoolSetupPage,
})

function detectPlatform(): 'mac' | 'windows' {
  if (typeof navigator === 'undefined') return 'mac'
  return /Windows/i.test(navigator.userAgent) ? 'windows' : 'mac'
}

function SchoolSetupPage() {
  const [platform, setPlatform] = useState<'mac' | 'windows'>('mac')
  const [connectionToken, setConnectionToken] = useState<ConnectionTokenResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  useEffect(() => {
    if (!connectionToken) {
      setTimeRemaining(null)
      return
    }
    const updateTimer = () => {
      const expiresAt = new Date(connectionToken.expiresAt).getTime()
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
      setTimeRemaining(remaining)
      if (remaining === 0) setConnectionToken(null)
    }
    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [connectionToken])

  const handleDownload = () => {
    const platformParam = platform === 'windows' ? '?platform=windows' : ''
    window.open(`${BACKEND_URL}/api/v1/downloads/helper-app${platformParam}`, '_blank')
  }

  const openDeepLink = (token: string) => {
    const encodedToken = encodeURIComponent(token)
    const link = document.createElement('a')
    link.href = `unbreakable://connect?token=${encodedToken}`
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleConnect = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await connectionTokenApi.generateConnectionToken()
      setConnectionToken(token)
      openDeepLink(token.token)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate connection token')
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">App Setup</h1>
        <p className="text-sm text-gray-500 mt-1">
          Download and connect the Unbreakable helper app to manage student devices.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Step 1: Download */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center shrink-0 text-sm font-semibold">
              1
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Download</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Install the helper app on each computer you'll use to enroll and supervise student devices.
          </p>

          <div className="bg-brand-light rounded-xl p-5 mb-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-white shrink-0">
                <img src="/unbreakable.png" alt="Unbreakable" className="w-full h-full object-contain" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Unbreakable Helper</h3>
                <p className="text-xs text-gray-500">
                  {platform === 'windows' ? 'Windows 10+' : 'macOS 12.0+'}
                </p>
              </div>
            </div>
            <button
              onClick={handleDownload}
              className="w-full py-2.5 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors flex items-center justify-center gap-2"
            >
              <Download size={16} />
              {platform === 'windows' ? 'Download for Windows' : 'Download for Mac'}
            </button>
          </div>

          <button
            onClick={() => setPlatform(platform === 'windows' ? 'mac' : 'windows')}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Looking for {platform === 'windows' ? 'macOS' : 'Windows'}?
          </button>

          <div className="mt-4 space-y-2">
            {[
              'Open the downloaded file',
              platform === 'windows' ? 'Follow the setup wizard' : 'Drag to Applications',
              'Launch the app',
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 text-xs font-medium">
                  {i + 1}
                </div>
                {text}
              </div>
            ))}
          </div>
        </div>

        {/* Step 2: Connect */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center shrink-0 text-sm font-semibold">
              2
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Connect</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Link the helper app to your school account. This generates a secure one-time token that authenticates the app.
          </p>

          {connectionToken && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 mb-4">
              <CheckCircle size={20} className="text-green-500 shrink-0" />
              <div>
                <p className="font-medium text-green-800 text-sm">Token generated</p>
                {timeRemaining !== null && (
                  <p className="text-xs text-green-600">Expires in {formatTime(timeRemaining)}</p>
                )}
              </div>
            </div>
          )}

          <button
            onClick={connectionToken ? () => openDeepLink(connectionToken.token) : handleConnect}
            disabled={loading}
            className="w-full py-2.5 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              'Connecting...'
            ) : (
              <>
                <LinkIcon size={16} />
                {connectionToken ? 'Open in Helper App' : 'Connect Helper App'}
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 mt-2">
            Generates a secure token and opens the Unbreakable helper app.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mt-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-2">How it works</h3>
            <ul className="space-y-1.5 text-sm text-gray-500">
              <li>Each staff member connects from their own account</li>
              <li>Tokens are single-use and expire after 10 minutes</li>
              <li>You can reconnect at any time by generating a new token</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
